import { useEffect, useState } from 'react';
import { Money } from '@commercetools/frontend-domain-types/product/Money';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import useCybersource from 'frontastic/hooks/useCybersource';
import { Props } from './interfaces';
import styles from './payment-information.module.scss';
import { CurrencyHelpers } from '../../../../../../../../helpers/currencyHelpers';
import { useFormat } from '../../../../../../../../helpers/hooks/useFormat';
import useI18n from '../../../../../../../../helpers/hooks/useI18n';
import { decodeCyberSourceToken } from '../../../../../checkout/components/steps/sections/payment/utils/utils';
import useResolveCCImage from '../../../../../checkout/hooks/useResolveCCImage';
import { CyberSourcePaymentDetails } from '../../../../../checkout/provider/payment/types';
import { PaymentMethod } from '../../interfaces';

const PaymentInformation = ({ order, className }: Props) => {
  const { formatMessage: formatOrdersMessage } = useFormat({ name: 'orders' });
  const { resolveCCImageByType } = useResolveCCImage();
  const { currency, defaultLocale, locale } = useI18n();

  const [cyberSourcePaymentDetails, setCyberSourcePaymentDetails] = useState<CyberSourcePaymentDetails>();
  const [cartDiscountTotal, setCartDiscountTotal] = useState(0);
  const [productDiscountTotal, setProductDiscountTotal] = useState(0);

  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);
  const [subTotal, setSubTotal] = useState(0);

  const { getSavedPaymentMethods } = useCybersource();

  const creditCardPayment = order?.payments?.find((payment) => payment.paymentMethod === PaymentMethod.CREDIT_CARD);
  const otherPayment = order?.payments?.find((payment) => payment.paymentMethod !== PaymentMethod.CREDIT_CARD);
  const billingAddress = order?.billingAddress;

  const getFormattedPrice = (price?: Money, isShippingPrice?: boolean) => {
    if (isShippingPrice && (!price || price?.centAmount === 0)) {
      return 'FREE';
    } else {
      return CurrencyHelpers.formatForCurrency(
        price?.centAmount ?? (0 as number),
        locale || defaultLocale,
        price?.currencyCode || currency,
        price?.fractionDigits || 2,
      );
    }
  };

  const setSavedPaymentMethodInfo = async () => {
    const paymentMethods = await getSavedPaymentMethods();
    if (paymentMethods) {
      const paymentMethod = paymentMethods.find(
        (payment) => payment.id === order?.payments?.[0]?.cyberSourcePaymentInstrumentId,
      );
      if (paymentMethod && paymentMethod.card) {
        setCyberSourcePaymentDetails({
          brand: paymentMethod.card.type,
          number: paymentMethod.card.number.replace('XXXXXXXXXXXX', '****-')?.replace('XXXXXXXXXXX', '****-'),
          expiryMonth: paymentMethod.card.expirationMonth,
          expiryYear: paymentMethod.card.expirationYear,
        });
      }
    }
    return undefined;
  };

  useEffect(() => {
    // get payment info
    if (order?.payments?.[0]?.cyberSourcePaymentInstrumentId) {
      setSavedPaymentMethodInfo();
    } else if (order?.payments?.[0]?.cyberSourceToken) {
      const token = order?.payments?.[0]?.cyberSourceToken;
      if (token) {
        setCyberSourcePaymentDetails(decodeCyberSourceToken(token));
      }
    }

    // get discount info
    if (order?.lineItems) {
      setProductDiscountTotal(order?.custom?.fields?.productDiscountTotal?.centAmount);
      setCartDiscountTotal(order?.custom?.fields?.cartDiscountTotal?.centAmount);
      setTax(order?.taxed?.totalTax?.centAmount ?? 0);
      setTotal(
        order?.taxed?.totalGross?.centAmount ? order?.taxed?.totalGross?.centAmount : order?.sum?.centAmount ?? 0,
      );
      setSubTotal(order?.custom?.fields?.subtotal?.centAmount);
    }
  }, [order?.lineItems, order?.payments]);

  return (
    <div className={classNames(styles['container'], className)}>
      <h3>
        {formatOrdersMessage({
          id: 'orderDetail.paymentInfo.title',
          defaultMessage: 'PAYMENT INFORMATION',
        })}
      </h3>

      {order?.sum && (
        <div className={styles['payment-info']}>
          {order?.sum?.centAmount !== 0 && (
            <div className={styles['info']}>
              {creditCardPayment && (
                <div className={styles['cc-info']}>
                  <h4>
                    <img
                      className="h-[20px] w-[32px]"
                      src={resolveCCImageByType(cyberSourcePaymentDetails?.brand?.toLowerCase() || '')}
                      alt="credit card logo"
                    />
                    {cyberSourcePaymentDetails?.number}
                  </h4>
                  <p className={styles['exp-date']}>
                    Exp. {cyberSourcePaymentDetails?.expiryMonth}/{cyberSourcePaymentDetails?.expiryYear}
                  </p>
                </div>
              )}
              {otherPayment && otherPayment.paymentMethod === PaymentMethod.PAY_PAL && <h4>PayPal</h4>}
              {otherPayment && otherPayment.paymentMethod === PaymentMethod.GOOGLE_PAY && <h4>Google Pay</h4>}
              {otherPayment && otherPayment.paymentMethod === PaymentMethod.APPLE_PAY && <h4>Apple Pay</h4>}
              {otherPayment && otherPayment.paymentMethod === PaymentMethod.BREAD_PAYMENT && <h4>Bread Pay</h4>}
              <p>
                {billingAddress?.firstName} {billingAddress?.lastName}
              </p>
              <p>{billingAddress?.streetName}</p>
              <p>{billingAddress?.additionalStreetInfo}</p>
              <p>
                {billingAddress?.city}, {billingAddress?.state} {billingAddress?.postalCode}
              </p>
              <p>{billingAddress?.phone}</p>
            </div>
          )}
          <div className={styles['info']}>
            <h4>
              {formatOrdersMessage({
                id: 'order.total.title',
                defaultMessage: 'Order Total',
              })}
            </h4>
            <p>
              {formatOrdersMessage({
                id: 'order.total.subtotal',
                defaultMessage: 'Subtotal:',
              })}{' '}
              {getFormattedPrice({ centAmount: subTotal, currencyCode: currency, fractionDigits: 2 })}{' '}
            </p>
            {productDiscountTotal !== undefined && productDiscountTotal > 0 && (
              <p className={styles.discounted}>
                {formatOrdersMessage({
                  id: 'order.total.you.save',
                  defaultMessage: 'You Save:',
                })}{' '}
                -
                {getFormattedPrice({
                  centAmount: productDiscountTotal,
                })}
              </p>
            )}

            <p>
              {cartDiscountTotal !== undefined && cartDiscountTotal > 0 && (
                <span data-testid="promo" className={styles.promo}>
                  <>
                    {formatOrdersMessage({
                      id: 'order.total.promotions',
                      defaultMessage: 'Promotions:',
                    })}{' '}
                    -
                    {getFormattedPrice({
                      centAmount: cartDiscountTotal,
                    })}
                  </>
                </span>
              )}
            </p>
            <p>
              {formatOrdersMessage({
                id: 'order.total.shipping',
                defaultMessage: 'Shipping:',
              })}{' '}
              {getFormattedPrice(order?.shippingInfo?.price, true)}
            </p>
            <p>
              {formatOrdersMessage({
                id: 'order.total.tax',
                defaultMessage: 'Tax:',
              })}{' '}
              {getFormattedPrice({ centAmount: tax, currencyCode: currency, fractionDigits: 2 })}{' '}
            </p>
            <p className={styles['total']}>
              {formatOrdersMessage({
                id: 'order.total.total',
                defaultMessage: 'Total:',
              })}{' '}
              {getFormattedPrice({ centAmount: total, currencyCode: currency, fractionDigits: 2 })}{' '}
            </p>
          </div>
        </div>
      )}

      {!order?.sum && (
        <div className={styles['payment-info']}>
          <div>
            <Skeleton baseColor="#cdcdcd" width={200} height={25} />
            <Skeleton baseColor="#cdcdcd" width={200} height={15} />
            <Skeleton baseColor="#cdcdcd" width={200} height={15} />
            <Skeleton baseColor="#cdcdcd" width={200} height={15} />
          </div>
          <div>
            <Skeleton baseColor="#cdcdcd" width={200} height={25} />
            <Skeleton baseColor="#cdcdcd" width={200} height={15} />
            <Skeleton baseColor="#cdcdcd" width={200} height={15} />
            <Skeleton baseColor="#cdcdcd" width={200} height={15} />
            <Skeleton baseColor="#cdcdcd" width={200} height={25} style={{ marginTop: '15px' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentInformation;
